import axios from 'axios';
import {
  Company,
  Team,
  Department,
  UserRole,
  CreateCompanyData,
  UpdateCompanyData,
  CreateTeamData,
  UpdateTeamData,
  CreateDepartmentData,
  UpdateDepartmentData,
  CreateUserRoleData,
} from '../types/organization.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: `${API_URL}/organizations`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token might be expired, redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface OrgPage<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export type OrgApiParams = Record<string, string | number | boolean | undefined>;

// Slim role edge for the organization tree: ids only — the tree already
// carries the full company/team/department objects, so names would be
// denormalized duplicates.
export interface OrgTreeUserRole {
  id: string;
  user: string;
  role: 'ADMIN' | 'MANAGER' | 'USER';
  company?: string | null;
  team?: string | null;
  department?: string | null;
}

export interface OrgTreeData {
  companies: Company[];
  teams: Team[];
  departments: Department[];
  userRoles: OrgTreeUserRole[];
}

// Snake_case API payload -> camelCase app model transforms
const toCompany = (company: any): Company => ({
  id: company.id,
  name: company.name,
  slug: company.slug,
  description: company.description,
  website: company.website,
  email: company.email,
  phone: company.phone,
  address: company.address,
  isActive: company.is_active,
  maxTeams: company.max_teams,
  maxUsers: company.max_users,
  teamsCount: company.teams_count,
  usersCount: company.users_count,
  createdBy: company.created_by,
  createdByEmail: company.created_by_email,
  createdAt: company.created_at,
  updatedAt: company.updated_at,
});

const toTeam = (team: any): Team => ({
  id: team.id,
  name: team.name,
  slug: team.slug,
  description: team.description,
  company: team.company,
  companyName: team.company_name,
  isActive: team.is_active,
  maxDepartments: team.max_departments,
  departmentsCount: team.departments_count,
  usersCount: team.users_count,
  createdBy: team.created_by,
  createdByEmail: team.created_by_email,
  createdAt: team.created_at,
  updatedAt: team.updated_at,
});

const toDepartment = (dept: any): Department => ({
  id: dept.id,
  name: dept.name,
  slug: dept.slug,
  description: dept.description,
  team: dept.team,
  teamName: dept.team_name,
  companyId: dept.company_id,
  companyName: dept.company_name,
  isActive: dept.is_active,
  usersCount: dept.users_count,
  createdBy: dept.created_by,
  createdByEmail: dept.created_by_email,
  createdAt: dept.created_at,
  updatedAt: dept.updated_at,
});

const toUserRole = (userRole: any): UserRole => ({
  id: userRole.id,
  user: userRole.user,
  userEmail: userRole.user_email,
  userFullName: userRole.user_full_name,
  role: userRole.role,
  company: userRole.company,
  team: userRole.team,
  department: userRole.department,
  organizationName: userRole.organization_name,
  companyName: userRole.company_name,
  teamName: userRole.team_name,
  departmentName: userRole.department_name,
  supervisor: userRole.supervisor,
  supervisorEmail: userRole.supervisor_email,
  supervisorFullName: userRole.supervisor_full_name,
  approvalChain: (userRole.approval_chain ?? []).map((entry: any) => ({
    id: entry.id,
    email: entry.email,
    fullName: entry.full_name,
  })),
  createdBy: userRole.created_by,
  createdByEmail: userRole.created_by_email,
  createdAt: userRole.created_at,
  updatedAt: userRole.updated_at,
});

// CamelCase app model -> snake_case API write payload transforms. DRF only
// binds its serializer field names, so camelCase keys like isActive are
// silently dropped on POST/PATCH — every write must go through these.
const ORG_WRITE_RENAMES: Record<string, string> = {
  isActive: 'is_active',
  maxTeams: 'max_teams',
  maxUsers: 'max_users',
  maxDepartments: 'max_departments',
};

const toWritePayload = (data: object): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) continue;
    payload[ORG_WRITE_RENAMES[key] ?? key] = value;
  }
  return payload;
};

export const toCompanyPayload = (data: CreateCompanyData | UpdateCompanyData): Record<string, unknown> =>
  toWritePayload(data);

export const toTeamPayload = (data: CreateTeamData | UpdateTeamData): Record<string, unknown> =>
  toWritePayload(data);

export const toDepartmentPayload = (data: CreateDepartmentData | UpdateDepartmentData): Record<string, unknown> =>
  toWritePayload(data);

class OrganizationService {
  // Company methods
  async getCompanies(): Promise<Company[]> {
    try {
      let allCompanies: any[] = [];
      let currentPage = 1;
      let hasMore = true;
      
      // Fetch all pages
      while (hasMore) {
        const response = await api.get('/companies/', {
          params: { page: currentPage, page_size: 100 }
        });
        const data = response.data;
        
        if (data.results && Array.isArray(data.results)) {
          allCompanies = [...allCompanies, ...data.results];
          // Check if there's more data
          hasMore = data.next !== null;
          currentPage++;
        } else if (Array.isArray(data)) {
          // If the response is not paginated
          allCompanies = data;
          hasMore = false;
        } else {
          hasMore = false;
        }
      }
    
    // Transform snake_case to camelCase
    return allCompanies.map(toCompany);
    } catch (error) {
      console.error('Error in getCompanies:', error);
      throw error;
    }
  }

  async getCompaniesPage(params: OrgApiParams): Promise<OrgPage<Company>> {
    const response = await api.get('/companies/', { params });
    const data = response.data;
    return {
      count: data.count,
      next: data.next,
      previous: data.previous,
      results: (data.results ?? []).map(toCompany),
    };
  }

  async getCompany(id: string): Promise<Company> {
    const response = await api.get(`/companies/${id}/`);
    return toCompany(response.data);
  }

  async createCompany(data: CreateCompanyData): Promise<Company> {
    const response = await api.post<Company>('/companies/', toCompanyPayload(data));
    return response.data;
  }

  async updateCompany(id: string, data: UpdateCompanyData): Promise<Company> {
    const response = await api.patch<Company>(`/companies/${id}/`, toCompanyPayload(data));
    return response.data;
  }

  async deleteCompany(id: string): Promise<void> {
    await api.delete(`/companies/${id}/`);
  }

  async getCompanyTeams(companyId: string): Promise<Team[]> {
    const response = await api.get(`/companies/${companyId}/teams/`);
    const rawData = response.data.results ?? response.data;
    return (Array.isArray(rawData) ? rawData : []).map(toTeam);
  }

  async getCompanyUsers(companyId: string): Promise<UserRole[]> {
    const response = await api.get<UserRole[]>(`/companies/${companyId}/users/`);
    return response.data;
  }

  async addUserToCompany(companyId: string, data: CreateUserRoleData): Promise<UserRole> {
    const response = await api.post<UserRole>(`/companies/${companyId}/add_user/`, data);
    return response.data;
  }

  // Team methods
  async getTeams(companyId?: string): Promise<Team[]> {
    const params = companyId ? { company: companyId } : {};
    const response = await api.get('/teams/', { params });

    // Handle pagination - results might be in response.data.results
    const rawData = response.data.results !== undefined ? response.data.results : response.data;

    if (!Array.isArray(rawData)) {
      console.error('Teams data is not an array:', rawData);
      return [];
    }

    // Transform snake_case to camelCase
    return rawData.map(toTeam);
  }

  async getAllTeams(): Promise<Team[]> {
    try {
      let allTeams: any[] = [];
      let currentPage = 1;
      let hasMore = true;

      // Fetch all pages
      while (hasMore) {
        const response = await api.get('/teams/', {
          params: { page: currentPage, page_size: 100 }
        });
        const data = response.data;

        if (data.results && Array.isArray(data.results)) {
          allTeams = [...allTeams, ...data.results];
          // Check if there's more data
          hasMore = data.next !== null;
          currentPage++;
        } else if (Array.isArray(data)) {
          // If the response is not paginated
          allTeams = data;
          hasMore = false;
        } else {
          hasMore = false;
        }
      }

      // Transform snake_case to camelCase
      return allTeams.map(toTeam);
    } catch (error) {
      console.error('Error in getAllTeams:', error);
      throw error;
    }
  }

  async getTeamsPage(params: OrgApiParams): Promise<OrgPage<Team>> {
    const response = await api.get('/teams/', { params });
    const data = response.data;
    return {
      count: data.count,
      next: data.next,
      previous: data.previous,
      results: (data.results ?? []).map(toTeam),
    };
  }

  async getTeam(id: string): Promise<Team> {
    const response = await api.get(`/teams/${id}/`);
    return toTeam(response.data);
  }

  async createTeam(data: CreateTeamData): Promise<Team> {
    const response = await api.post<Team>('/teams/', toTeamPayload(data));
    return response.data;
  }

  async updateTeam(id: string, data: UpdateTeamData): Promise<Team> {
    const response = await api.patch<Team>(`/teams/${id}/`, toTeamPayload(data));
    return response.data;
  }

  async deleteTeam(id: string): Promise<void> {
    await api.delete(`/teams/${id}/`);
  }

  async getTeamDepartments(teamId: string): Promise<Department[]> {
    const response = await api.get(`/teams/${teamId}/departments/`);
    const rawData = response.data.results ?? response.data;
    return (Array.isArray(rawData) ? rawData : []).map(toDepartment);
  }

  async getTeamUsers(teamId: string): Promise<UserRole[]> {
    const response = await api.get<UserRole[]>(`/teams/${teamId}/users/`);
    return response.data;
  }

  // Department methods
  async getDepartments(teamId?: string): Promise<Department[]> {
    const params = teamId ? { team: teamId } : {};
    const response = await api.get('/departments/', { params });

    // Handle pagination - results might be in response.data.results
    const rawData = response.data.results !== undefined ? response.data.results : response.data;

    if (!Array.isArray(rawData)) {
      console.error('Departments data is not an array:', rawData);
      return [];
    }

    // Transform snake_case to camelCase
    return rawData.map(toDepartment);
  }

  async getAllDepartments(): Promise<Department[]> {
    try {
      let allDepartments: any[] = [];
      let currentPage = 1;
      let hasMore = true;

      // Fetch all pages
      while (hasMore) {
        const response = await api.get('/departments/', {
          params: { page: currentPage, page_size: 100 }
        });
        const data = response.data;

        if (data.results && Array.isArray(data.results)) {
          allDepartments = [...allDepartments, ...data.results];
          // Check if there's more data
          hasMore = data.next !== null;
          currentPage++;
        } else if (Array.isArray(data)) {
          // If the response is not paginated
          allDepartments = data;
          hasMore = false;
        } else {
          hasMore = false;
        }
      }

      // Transform snake_case to camelCase
      return allDepartments.map(toDepartment);
    } catch (error) {
      console.error('Error in getAllDepartments:', error);
      throw error;
    }
  }

  async getDepartmentsPage(params: OrgApiParams): Promise<OrgPage<Department>> {
    const response = await api.get('/departments/', { params });
    const data = response.data;
    return {
      count: data.count,
      next: data.next,
      previous: data.previous,
      results: (data.results ?? []).map(toDepartment),
    };
  }

  async getDepartment(id: string): Promise<Department> {
    const response = await api.get(`/departments/${id}/`);
    return toDepartment(response.data);
  }

  async createDepartment(data: CreateDepartmentData): Promise<Department> {
    const response = await api.post<Department>('/departments/', toDepartmentPayload(data));
    return response.data;
  }

  async updateDepartment(id: string, data: UpdateDepartmentData): Promise<Department> {
    const response = await api.patch<Department>(`/departments/${id}/`, toDepartmentPayload(data));
    return response.data;
  }

  async deleteDepartment(id: string): Promise<void> {
    await api.delete(`/departments/${id}/`);
  }

  async getDepartmentUsers(departmentId: string): Promise<UserRole[]> {
    const response = await api.get<UserRole[]>(`/departments/${departmentId}/users/`);
    return response.data;
  }

  // User Role methods
  async getUserRoles(): Promise<UserRole[]> {
    try {
      let allUserRoles: any[] = [];
      let currentPage = 1;
      let hasMore = true;

      // Fetch all pages
      while (hasMore) {
        const response = await api.get('/roles/', {
          params: { page: currentPage, page_size: 100 }
        });
        const data = response.data;

        if (data.results && Array.isArray(data.results)) {
          allUserRoles = [...allUserRoles, ...data.results];
          // Check if there's more data
          hasMore = data.next !== null;
          currentPage++;
        } else if (Array.isArray(data)) {
          // If the response is not paginated
          allUserRoles = data;
          hasMore = false;
        } else {
          hasMore = false;
        }
      }

      // Transform snake_case to camelCase
      return allUserRoles.map((userRole: any) => ({
        id: userRole.id,
        user: userRole.user,
        userEmail: userRole.user_email,
        userFullName: userRole.user_full_name,
        role: userRole.role,
        company: userRole.company,
        team: userRole.team,
        department: userRole.department,
        organizationName: userRole.organization_name,
        createdBy: userRole.created_by,
        createdByEmail: userRole.created_by_email,
        createdAt: userRole.created_at,
        updatedAt: userRole.updated_at,
      }));
    } catch (error) {
      console.error('Error in getUserRoles:', error);
      throw error;
    }
  }

  async getMyRoles(): Promise<UserRole[]> {
    const response = await api.get<UserRole[]>('/roles/my_roles/');
    return response.data;
  }

  async getCurrentUserRoles(): Promise<UserRole[]> {
    try {
      const response = await api.get('/roles/my_roles/');
      const data = response.data;
      
      if (!Array.isArray(data)) {
        return [];
      }
      
      // Transform snake_case to camelCase including new fields
      return data.map((userRole: any) => ({
        id: userRole.id,
        user: userRole.user,
        userEmail: userRole.user_email,
        userFullName: userRole.user_full_name,
        role: userRole.role,
        company: userRole.company,
        team: userRole.team,
        department: userRole.department,
        organizationName: userRole.organization_name,
        companyName: userRole.company_name,
        teamName: userRole.team_name,
        departmentName: userRole.department_name,
        createdBy: userRole.created_by,
        createdByEmail: userRole.created_by_email,
        createdAt: userRole.created_at,
        updatedAt: userRole.updated_at,
      }));
    } catch (error) {
      console.error('Failed to get current user roles:', error);
      return [];
    }
  }

  async getUserRolesFor(userId: string): Promise<UserRole[]> {
    // Page-loop the whole collection — the server ignores page_size, so a
    // single request would silently truncate a heavily-assigned user's
    // roles at the server page size.
    let allRoles: any[] = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await api.get('/roles/', {
        params: { user: userId, page: currentPage }
      });
      const data = response.data;

      if (data.results && Array.isArray(data.results)) {
        allRoles = [...allRoles, ...data.results];
        hasMore = data.next !== null;
        currentPage++;
      } else if (Array.isArray(data)) {
        // If the response is not paginated
        allRoles = data;
        hasMore = false;
      } else {
        hasMore = false;
      }
    }

    return allRoles.map(toUserRole);
  }

  async createUserRole(data: CreateUserRoleData): Promise<UserRole> {
    const response = await api.post<UserRole>('/roles/', data);
    return response.data;
  }

  async updateUserRole(id: string, data: Partial<CreateUserRoleData>): Promise<UserRole> {
    const response = await api.patch<UserRole>(`/roles/${id}/`, data);
    return response.data;
  }

  async deleteUserRole(id: string): Promise<void> {
    await api.delete(`/roles/${id}/`);
  }

  // Organization tree
  async getOrganizationTree(): Promise<{
    companies: Company[];
    teams: Team[];
    departments: Department[];
    userRoles: UserRole[];
  }> {
    const response = await api.get('/roles/organization_tree/');
    return response.data;
  }

  // Transformed organization tree: the endpoint returns flat snake_case
  // arrays under a `user_roles` key, so the raw getOrganizationTree above
  // never actually matched its declared camelCase shape. New code should
  // use this method.
  async getOrganizationTreeData(): Promise<OrgTreeData> {
    const response = await api.get('/roles/organization_tree/');
    const data = response.data;
    return {
      companies: (data.companies ?? []).map(toCompany),
      teams: (data.teams ?? []).map(toTeam),
      departments: (data.departments ?? []).map(toDepartment),
      userRoles: (data.user_roles ?? []).map((role: any): OrgTreeUserRole => ({
        id: role.id,
        user: role.user,
        role: role.role,
        company: role.company,
        team: role.team,
        department: role.department,
      })),
    };
  }

  // Filtered organization methods for specific users
  async getCompaniesForUser(userId: string): Promise<Company[]> {
    const response = await api.get(`/companies/for-user/${userId}/`);
    const data = response.data.results || response.data;

    // Transform snake_case to camelCase
    return data.map(toCompany);
  }

  async getTeamsForUser(userId: string, companyId?: string): Promise<Team[]> {
    const response = await api.get(`/teams/for-user/${userId}/`);
    const rawData = response.data.results !== undefined ? response.data.results : response.data;
    
    if (!Array.isArray(rawData)) {
      return [];
    }
    
    // Filter by company if specified
    let filteredData = rawData;
    if (companyId) {
      filteredData = rawData.filter((team: any) => team.company === companyId);
    }
    
    // Transform snake_case to camelCase
    return filteredData.map(toTeam);
  }

  async getDepartmentsForUser(userId: string, teamId?: string): Promise<Department[]> {
    const response = await api.get(`/departments/for-user/${userId}/`);
    const rawData = response.data.results !== undefined ? response.data.results : response.data;
    
    if (!Array.isArray(rawData)) {
      return [];
    }
    
    // Filter by team if specified
    let filteredData = rawData;
    if (teamId) {
      filteredData = rawData.filter((dept: any) => dept.team === teamId);
    }
    
    // Transform snake_case to camelCase
    return filteredData.map(toDepartment);
  }
}

const organizationService = new OrganizationService();
export default organizationService;